/**
 * useInsiSearch — orchestration hook for the INSi search modal (T06).
 *
 * Wraps the Stage-1 typed Apollo wrappers (`useSearchInsByTraits` +
 * `useFindPatientByIns`) and exposes a small state machine the modal renders
 * against:
 *
 *   idle | loading | cr00 | cr01 | cr02 | error
 *
 * Behaviour rules enforced here (tech_design_spec §6.1, handoff §2.1 / §8):
 *  - FIRST `search` call sends ONLY nomNaissance / prenoms / sexe / dateNaissance
 *    — never `lieuNaissance` (EF_INS10_05).
 *  - `retryWithLieu` re-runs the SAME traits plus the INSEE `lieuNaissance`
 *    disambiguator (the CR02 retry path).
 *  - `prenoms` is the caller-supplied list (already split from the single
 *    "First name(s)" field), each trait transliterated before being sent.
 *  - result `code` (CR00 / CR01 / CR02 / TECHNICAL_ERROR) maps to the matching
 *    state; `error.code === 'server_error'` (and TECHNICAL_ERROR / thrown
 *    errors) map to the transient `error` state (handoff §8 / §10).
 *  - on CR00 with an `existingPatientId`, `findPatientByIns` resolves the
 *    existing-patient summary for the dedup affordance (D4).
 */
import { useCallback, useRef, useState } from 'react';

import { useSearchInsByTraits, useFindPatientByIns } from 'services/insi/operations';
import { transliterateTrait } from 'services/insi/traitsValidation';
import {
  useHomologation,
  buildHomologationHeaders,
} from 'components/insi/HomologationContext';
import {
  InsiCandidate,
  PatientSummary,
  SearchInsByTraitsInput,
  Sexe,
} from 'services/insi/types';

export type InsiSearchState =
  | 'idle'
  | 'loading'
  | 'cr00'
  | 'cr01'
  | 'cr02'
  | 'error';

/** The minimum traits the modal collects on the first call. */
export interface InsiSearchTraits {
  nomNaissance: string;
  /** Already split from the "First name(s)" field; index 0 = first usual prénom. */
  prenoms: string[];
  sexe: Sexe;
  dateNaissance: string; // 'YYYY-MM-DD'
}

export interface UseInsiSearch {
  state: InsiSearchState;
  /** The single CR00 candidate (null in every other state). */
  candidate: InsiCandidate | null;
  /** Local patient already holding this INS (D4) — id only, from the search. */
  existingPatientId: string | null;
  /** Resolved summary of that existing patient (via findPatientByIns). */
  existingPatient: PatientSummary | null;
  /** Optional human-readable message echoed from the téléservice. */
  message: string | null;
  /** The traits used for the last search (so the modal can echo them on CR00). */
  lastTraits: InsiSearchTraits | null;
  /** FIRST call — never sends lieuNaissance. */
  search: (traits: InsiSearchTraits) => Promise<void>;
  /** CR02 retry — same traits + INSEE lieuNaissance. */
  retryWithLieu: (lieuNaissance: string) => Promise<void>;
  /** Re-run the last search verbatim (the `error`-state Retry affordance). */
  retry: () => Promise<void>;
  /** Reset back to idle (e.g. when the modal closes). */
  reset: () => void;
}

const transliterateTraits = (traits: InsiSearchTraits): InsiSearchTraits => ({
  nomNaissance: transliterateTrait(traits.nomNaissance),
  prenoms: (traits.prenoms || [])
    .map(transliterateTrait)
    .filter((p) => p.length > 0),
  sexe: traits.sexe,
  dateNaissance: traits.dateNaissance,
});

export const useInsiSearch = (): UseInsiSearch => {
  const [searchInsByTraits] = useSearchInsByTraits();
  const [findPatientByIns] = useFindPatientByIns();
  const { scenarioId, captureRequested } = useHomologation();

  const [state, setState] = useState<InsiSearchState>('idle');
  const [candidate, setCandidate] = useState<InsiCandidate | null>(null);
  const [existingPatientId, setExistingPatientId] = useState<string | null>(
    null
  );
  const [existingPatient, setExistingPatient] = useState<PatientSummary | null>(
    null
  );
  const [message, setMessage] = useState<string | null>(null);
  const [lastTraits, setLastTraits] = useState<InsiSearchTraits | null>(null);

  // Track the lieuNaissance the last (retry) call carried, so `retry()` can
  // replay the exact same payload on an `error`-state retry.
  const lastLieuRef = useRef<string | undefined>(undefined);

  const resolveExistingPatient = useCallback(
    async (cand: InsiCandidate, patientId: string) => {
      try {
        const { data } = await findPatientByIns({
          variables: {
            insValue: cand.ins.value,
            insType: cand.ins.type,
          },
        });
        setExistingPatient(data?.insi?.findPatientByIns ?? null);
      } catch {
        // Dedup is non-blocking — a failed lookup just hides the affordance.
        setExistingPatient(null);
      }
    },
    [findPatientByIns]
  );

  const runSearch = useCallback(
    async (traits: InsiSearchTraits, lieuNaissance?: string) => {
      setState('loading');
      setCandidate(null);
      setExistingPatientId(null);
      setExistingPatient(null);
      setMessage(null);

      const transliterated = transliterateTraits(traits);
      setLastTraits(transliterated);
      lastLieuRef.current = lieuNaissance;

      const record: SearchInsByTraitsInput = {
        nomNaissance: transliterated.nomNaissance,
        prenoms: transliterated.prenoms,
        sexe: transliterated.sexe,
        dateNaissance: transliterated.dateNaissance,
        // NEVER on the first call (EF_INS10_05); only added on the CR02 retry.
        ...(lieuNaissance ? { lieuNaissance } : {}),
      };

      // Homologation/CNDA only: inject the test-scenario headers (never in the
      // GraphQL body, never in production — gated by the build flag). Attached
      // here, so ONLY the search operation carries them.
      const homologationHeaders = buildHomologationHeaders({
        scenarioId,
        captureRequested,
      });

      try {
        const { data } = await searchInsByTraits({
          variables: { record },
          ...(Object.keys(homologationHeaders).length
            ? { context: { headers: homologationHeaders } }
            : {}),
        });
        const payload = data?.insi?.searchInsByTraits;

        // Transient server error (handoff §8/§10): treat as retryable.
        if (payload?.error?.code === 'server_error') {
          setMessage(payload.error.message ?? null);
          setState('error');
          return;
        }

        const result = payload?.result;
        if (!payload?.ok || !result) {
          setMessage(payload?.error?.message ?? null);
          setState('error');
          return;
        }

        setMessage(result.message ?? null);

        switch (result.code) {
          case 'CR00': {
            const cand = result.candidates?.[0] ?? null;
            setCandidate(cand);
            setExistingPatientId(result.existingPatientId ?? null);
            setState('cr00');
            if (cand && result.existingPatientId) {
              await resolveExistingPatient(cand, result.existingPatientId);
            }
            return;
          }
          case 'CR01':
            setState('cr01');
            return;
          case 'CR02':
            setState('cr02');
            return;
          case 'TECHNICAL_ERROR':
          default:
            setState('error');
            return;
        }
      } catch (e) {
        setState('error');
      }
    },
    [searchInsByTraits, resolveExistingPatient, scenarioId, captureRequested]
  );

  const search = useCallback(
    (traits: InsiSearchTraits) => runSearch(traits),
    [runSearch]
  );

  const retryWithLieu = useCallback(
    (lieuNaissance: string) => {
      if (!lastTraits) {
        return Promise.resolve();
      }
      return runSearch(lastTraits, lieuNaissance);
    },
    [runSearch, lastTraits]
  );

  const retry = useCallback(() => {
    if (!lastTraits) {
      return Promise.resolve();
    }
    return runSearch(lastTraits, lastLieuRef.current);
  }, [runSearch, lastTraits]);

  const reset = useCallback(() => {
    setState('idle');
    setCandidate(null);
    setExistingPatientId(null);
    setExistingPatient(null);
    setMessage(null);
    setLastTraits(null);
    lastLieuRef.current = undefined;
  }, []);

  return {
    state,
    candidate,
    existingPatientId,
    existingPatient,
    message,
    lastTraits,
    search,
    retryWithLieu,
    retry,
    reset,
  };
};

export default useInsiSearch;
